'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { toast } from '@/components/ui/use-toast';
import { authPoliciesKeyPrefix, authenticationPolicyOrderKey, matchesAuthenticationPolicyIds, sortPoliciesForDisplay, toAuthenticationPolicyReorderOrderedIds, useAuthenticationPolicyList, useDeleteAuthenticationPolicy, useReorderAuthenticationPolicies } from '@/features/authentication-policy';
import { PolicySectionHeader } from '@/features/identity-policy';
import { handleApiError } from '@/lib/api/error';
import type { AuthenticationPolicySummary } from '@/lib/api/orpc/contracts/authentication-policy';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { PoliciesTable } from './-components/PoliciesTable';
export const Route = createFileRoute('/_staff/settings/authentication/_layout/policies/')({
  component: AuthPoliciesPage
});
function AuthPoliciesPage() {
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: authPoliciesKeyPrefix
  });
  const {
    data,
    isPending,
    isError
  } = useAuthenticationPolicyList();
  const reorderMutation = useReorderAuthenticationPolicies();
  const deleteMutation = useDeleteAuthenticationPolicy();
  const [draft, setDraft] = useState<AuthenticationPolicySummary[] | null>(null);
  const [baselineOrderKey, setBaselineOrderKey] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AuthenticationPolicySummary | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  // stale 破棄を通知するキー。effect では toast のみ行い setState しない
  const [staleToastKey, setStaleToastKey] = useState<string | null>(null);
  const toastedStaleKeysRef = useRef(new Set<string>());

  // サーバ側で ID 集合が変わったら draft を捨てる（render 中の調整）
  const serverItems = data?.items;
  if (draft != null && serverItems != null && !matchesAuthenticationPolicyIds(draft, serverItems)) {
    const token = serverItems.map(policy => policy.id).join(',');
    setDraft(null);
    setBaselineOrderKey(null);
    setErrorMessage('');
    if (staleToastKey !== token) {
      setStaleToastKey(token);
    }
  }
  useEffect(() => {
    if (staleToastKey == null) return;
    // Strict Mode / t 変更での二重・再発火を抑止
    if (toastedStaleKeysRef.current.has(staleToastKey)) return;
    toastedStaleKeysRef.current.add(staleToastKey);
    toast({
      description: t($ => $.list.reorder.staleDiscarded),
      variant: 'destructive'
    });
  }, [staleToastKey, t]);
  if (isPending) return <ViewportLoader />;
  if (isError || data == null) {
    return <VStack className="w-full gap-4 px-12 py-6">
        <PolicySectionHeader iconName="ShieldCheckIcon" title={t($ => $.title)} description={t($ => $.description)} />
        <AlertCard variant="mutedError" description={t($ => $.list.loadError)} />
      </VStack>;
  }
  const validDraft = draft != null && matchesAuthenticationPolicyIds(draft, data.items) ? draft : null;
  const displayed = validDraft ?? sortPoliciesForDisplay(data.items);
  const isReordering = validDraft != null;
  const reorderableCount = displayed.filter(policy => !policy.isDefault).length;
  const enterReorder = () => {
    const sorted = sortPoliciesForDisplay(data.items);
    toastedStaleKeysRef.current.clear();
    setStaleToastKey(null);
    setDraft(sorted);
    setBaselineOrderKey(authenticationPolicyOrderKey(sorted));
    setErrorMessage('');
  };
  const exitReorder = () => {
    setDraft(null);
    setBaselineOrderKey(null);
    setErrorMessage('');
  };
  const handleSaveOrder = async () => {
    if (validDraft == null) return;
    setErrorMessage('');
    if (baselineOrderKey != null && authenticationPolicyOrderKey(data.items) !== baselineOrderKey) {
      exitReorder();
      toast({
        description: t($ => $.list.reorder.conflict),
        variant: 'destructive'
      });
      return;
    }
    const orderedIds = toAuthenticationPolicyReorderOrderedIds(validDraft);
    if (orderedIds.length === 0) {
      exitReorder();
      return;
    }
    try {
      await reorderMutation.mutateAsync({
        orderedIds
      });
      exitReorder();
      toast({
        description: t($ => $.toast.reordered)
      });
    } catch (error) {
      setErrorMessage(handleApiError(error, {
        language: i18n.language
      }).message);
    }
  };
  const handleDelete = async () => {
    if (deleteTarget == null) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id
      });
      setDeleteTarget(null);
      toast({
        description: t($ => $.toast.deleted)
      });
    } catch (error) {
      toast({
        description: handleApiError(error, {
          language: i18n.language
        }).message,
        variant: 'destructive'
      });
    }
  };
  return <VStack className="w-full gap-4 px-12 py-6">
      <PolicySectionHeader iconName="ShieldCheckIcon" title={t($ => $.title)} description={t($ => $.description)} />

      {errorMessage !== '' && <Text variant="labelSmallReg" className="text-error-on-neutral-fill">
          {errorMessage}
        </Text>}

      <PoliciesTable rows={displayed} isPending={false} isReordering={isReordering} reorderPending={reorderMutation.isPending} reorderableCount={reorderableCount} onEnterReorder={enterReorder} onExitReorder={exitReorder} onSaveReorder={() => void handleSaveOrder()} onDraftChange={setDraft} onDelete={setDeleteTarget} />

      <ConfirmActionDialog isOpen={deleteTarget != null} isLoading={deleteMutation.isPending} title={t($ => $.delete.title)} description={(deleteTarget?.attachmentCount ?? 0) > 0 ? t($ => $.delete.descriptionReferenced, {
      count: deleteTarget?.attachmentCount ?? 0
    }) : t($ => $.delete.descriptionUnreferenced)} okLabel={t($ => $.delete.confirm)} okVariant="danger" onClose={() => setDeleteTarget(null)} onConfirm={() => {
      void handleDelete();
    }} />
    </VStack>;
}